import React from 'react';
import { Progress } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCertificate } from '@fortawesome/free-solid-svg-icons';
import { booleanTranslator } from '@s20/common/util/string';
import { httpClient } from '@s20/services/httpClient';
import {
    createDataSource,
    createGridOptions,
    DataGrid,
    LinkCellRenderer,
    TextFilterDefaults,
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import CommonContext, { ApiRoutes, agGridConstants, ApplicationPermissions } from '../Common';
import {
    PageHeading,
    onReactSelectChanged,
    onFieldChange,
    PageWrap,
    toasty,
} from '../common/forms/FormElements';
import { handleFormSaveError } from '../common/forms/ValidationError';
import CertificationForm from './CertificationForm';
import { CertificationViewModel } from './Certification';

class CertificationIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        this.state = {
            loading: true,
            selectedCertification: null,
            showCertificationForm: false,
            saving: false,
            errors: null,
        };

        this.onChange = onFieldChange.bind(this);
        this.onSelectChange = onReactSelectChanged.bind(this);
        this.handleSaveError = this.handleSaveError.bind(this);
    }

    componentDidMount() {
        this.populateState();
    }

    populateState() {
        const gridOptions = createGridOptions(this);

        gridOptions.components = {
            nameRenderer: LinkCellRenderer,
            selectFilter: DataGridSelectFilter,
            selectFloatingFilter: DataGridSelectFloatingFilter,
        };

        const yesNo = [
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' },
        ];

        const yesNoFilterParams = {
            suppressFilterButton: true,
            options: yesNo,
            optionsLabel: 'label',
            optionsValue: 'value',
            initialFilterValue: null,
        };

        gridOptions.columnDefs = [
            {
                colId: 'Name',
                sortable: true,
                headerName: 'Name',
                field: 'name',
                filter: agGridConstants.columnFilterTypes.text,
                floatingFilter: true,
                filterParams: TextFilterDefaults,
                sort: { direction: 'asc', priority: 0 },
                cellRenderer: 'nameRenderer',
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                cellRendererParams: {
                    clicked: this.onEditCertification,
                    nameField: 'name',
                    idField: 'id',
                    title: 'View this Certification',
                },
            },
            {
                colId: 'Abbreviation',
                sortable: true,
                headerName: 'Abbreviation',
                field: 'abbreviation',
                filter: agGridConstants.columnFilterTypes.text,
                floatingFilter: true,
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'willExpire',
                sortable: true,
                headerName: 'Expiration',
                field: 'willExpire',
                cellRenderer: booleanTranslator,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: yesNoFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: yesNoFilterParams,
            },
            {
                colId: 'IsActive',
                sortable: true,
                headerName: 'Active',
                field: 'isActive',
                cellRenderer: booleanTranslator,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: yesNoFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: yesNoFilterParams,
            },
        ];

        const dataSource = createDataSource(
            ApiRoutes.certification.search(),
            gridOptions,
        );

        this.setState({
            loading: false,
            gridOptions,
            dataSource,
        });
    }

    onAddCertification = () => {
        this.setState({
            selectedCertification: new CertificationViewModel(),
            showCertificationForm: true,
        });
    };

    onEditCertification = async (certificationId) => {
        const selectedCertification = await httpClient.js(
            ApiRoutes.certification.byId(certificationId),
        );

        this.setState({
            selectedCertification,
            showCertificationForm: true,
        });
    };

    onCertificationSaveCallback = async () => {
        this.setState({ saving: true });
        const { selectedCertification } = this.state;

        const isEdit = !!(selectedCertification.id && selectedCertification.id > 0);
        const saveUrl = isEdit
            ? ApiRoutes.certification.update(selectedCertification.id)
            : ApiRoutes.certification.create();

        const response = await httpClient
            .andGetResponse(
                httpClient.types.post,
                saveUrl,
                selectedCertification,
                'Error Saving Certification',
                () => this.setState({ saving: false }),
            )
            .catch(this.handleSaveError);

        if (response && response > 0) {
            toasty.success(
                'Certification Saved',
                'Certification saved successfully.',
            );

            this.setState({
                selectedCertification: null,
                showCertificationForm: false,
                saving: false,
            });

            this.state.gridApi.setDatasource(this.state.dataSource);
        }
    };

    onCertificationFormClosed = () => {
        this.setState({
            selectedCertification: null,
            showCertificationForm: false,
        });
    };

    onChange = onFieldChange;
    onSelectChange = onReactSelectChanged;

    handleSaveError = (err) => {
        console.debug(err);
        handleFormSaveError(this, err);
    };

    render() {
        if (this.state.loading) {
            return <Progress />;
        }
        const exportCertPermissions = this.context.checkUserHasPermission(ApplicationPermissions.certification_create);

        const {
            rowData,
            gridOptions,
            showCertificationForm,
            selectedCertification,
        } = this.state;

        return (
            <PageWrap>
                <PageHeading>
                    <FontAwesomeIcon
                        icon={faCertificate}
                        className="mr-2 text-muted"
                    />
                    <span>Certifications</span>
                </PageHeading>

                <DataGridToolbar
                    entity="certification"
                    hideAdd={!exportCertPermissions}
                    gridOptions={this.state.gridOptions}
                    hideExcelButton
                    gridApi={this.state.gridApi}
                    dataSource={this.state.dataSource}
                    onAdd={this.onAddCertification}
                    addLabel="Add Certification"
                    serverExport={{
                        apiPath: ApiRoutes.certification.excelExport(),
                        filePrefix: 'Certifications',
                    }}
                    serverExportDisabled={showCertificationForm}
                />

                <DataGrid
                    domLayout="normal"
                    rowData={rowData}
                    gridOptions={gridOptions}
                    gridStatus={this.state.gridStatus}
                />

                <CertificationForm
                    selectedCertification={selectedCertification}
                    onChange={this.onChange}
                    onSelectChange={this.onSelectChange}
                    onClose={this.onCertificationFormClosed}
                    show={showCertificationForm}
                    onSaveCallback={this.onCertificationSaveCallback}
                />
            </PageWrap>
        );
    }
}

export default CertificationIndex;
